// Draw a branded candlestick chart from a price series onto a canvas → returns a PNG data URL.
// Used to auto-grab a token's chart for the Newsroom share card (no external deps, no CORS taint).
export function renderPriceChart(series, { symbol = "", timeline = null, width = 900, height = 420 } = {}) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  const ctx = c.getContext("2d");

  // background
  ctx.fillStyle = "#05070c";
  ctx.fillRect(0, 0, width, height);

  const padL = 16, padR = 16, padT = 46, padB = 26;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  const highs = series.map((s) => s.h);
  const lows = series.map((s) => s.l);
  let hi = Math.max(...highs);
  let lo = Math.min(...lows);
  if (hi === lo) hi = lo * 1.0001 + 1e-12;
  const pad = (hi - lo) * 0.06;
  hi += pad; lo -= pad;
  const y = (p) => padT + plotH - ((p - lo) / (hi - lo)) * plotH;

  // grid
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const gy = padT + (plotH / 4) * i;
    ctx.beginPath(); ctx.moveTo(padL, gy); ctx.lineTo(width - padR, gy); ctx.stroke();
  }

  const n = series.length;
  const step = plotW / n;
  const cw = Math.max(1, Math.min(10, step * 0.62));
  series.forEach((s, i) => {
    const cx = padL + step * i + step / 2;
    const up = s.c >= s.o;
    ctx.strokeStyle = up ? "#00ff88" : "#ff4d5e";
    ctx.fillStyle = up ? "#00ff88" : "#ff4d5e";
    // wick
    ctx.beginPath(); ctx.moveTo(cx, y(s.h)); ctx.lineTo(cx, y(s.l)); ctx.stroke();
    // body
    const yo = y(s.o), yc = y(s.c);
    const top = Math.min(yo, yc);
    const h = Math.max(1, Math.abs(yc - yo));
    ctx.fillRect(cx - cw / 2, top, cw, h);
  });

  // markers: peak + turn
  const markerAt = (idx, color, label) => {
    if (idx == null || idx < 0 || idx >= n) return;
    const cx = padL + step * idx + step / 2;
    const cy = y(series[idx].h);
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(cx, cy, 4.5, 0, Math.PI * 2); ctx.fill();
    ctx.font = "600 12px ui-monospace, monospace";
    ctx.fillStyle = color;
    const tw = ctx.measureText(label).width;
    ctx.fillText(label, Math.min(Math.max(cx - tw / 2, padL), width - padR - tw), Math.max(cy - 8, padT + 12));
  };
  if (timeline) {
    markerAt(timeline?.peak?.idx, "#00ff88", "PEAK");
    markerAt(timeline?.turn?.idx, "#ff4d5e", "THE TURN");
  }

  // header
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 20px ui-sans-serif, system-ui";
  ctx.fillText(symbol ? `$${String(symbol).toUpperCase()}` : "PRICE", padL, 30);
  ctx.fillStyle = "#00ff88";
  ctx.font = "600 12px ui-monospace, monospace";
  const tag = "ALPHAAPEING · DEGEN SCORE";
  const tw = ctx.measureText(tag).width;
  ctx.fillText(tag, width - padR - tw, 30);

  return c.toDataURL("image/png");
}
